import formatComma from "react-lib/utils/formatComma";

import { ORDER_STATUS, type State } from "../common/sequence/PatientOrdersV4";
import {
  EDITABLE_KINDS,
  type DoctorOption,
  type EditableKind,
  type OrderMasterOptions,
  type SheetLine,
} from "../common/sequence/OrderSheetV4";

import {
  ORDER_DEF,
  SHEET_CATEGORIES,
  URGENCY_LABELS,
  allocatePackages,
  formatLabSummary,
  formatPackage,
  getCategory,
  getStep,
  parseStatus,
  priceOf,
  sheetGroupOf,
  type DoneRow,
  type LabTest,
  type SheetCategory,
  type SheetRow,
} from "./ordersModelV4";
import { stripHtml } from "./resultModelV4";
import { sheetAmount, sheetMissing, sheetSummary } from "./orderSheetLineModelV4";

// ---------- Types ----------
type Sequence = NonNullable<State["PatientOrdersV4Sequence"]>;

type Order = NonNullable<Sequence["orderList"]>[number];

type LabOrder = NonNullable<Sequence["labOrderList"]>[number];

type QueueOrder = NonNullable<NonNullable<Sequence["encounter"]>["doctor_orders"]>[number];

export type OrderRow = ReturnType<typeof toOrderRow>;

export type DoneGroup = {
  category: SheetCategory;
  rows: DoneRow[];
  cancelled: number;
  total: number;
};

type SheetGroupsParams = {
  orderRows: OrderRow[];
  editingOrderIds: Set<number | undefined>;
  editingFeeIds: Set<number>;
  draftItems: any[];
  labTestInfo: Record<string, LabTest>;
  sheetLines: SheetLine[];
  doctorOptions: DoctorOption[];
  masterOptions?: OrderMasterOptions;
  doctorFeeList: NonNullable<Sequence["doctorFeeList"]>;
  miscList: NonNullable<Sequence["miscList"]>;
  pkgUse: ReturnType<typeof allocatePackages>;
};

type DoneRowsParams = Pick<
  SheetGroupsParams,
  "orderRows" | "editingOrderIds" | "editingFeeIds" | "doctorFeeList" | "miscList"
>;

// ---------- Const ----------
/** สถานะของ DF / เบ็ดเตล็ด ที่ยืนยันแล้ว */
const FEE_STATUS = "Ordered";

// ---------- Order ----------
/** สถานะ / สิทธิ์แก้-ยกเลิก / รายการ ของ Order 1 ตัว */
export const toOrderRow = (
  order: Order,
  labOrders: Map<number | undefined, LabOrder>,
  queueOrders: Map<number | undefined, QueueOrder>,
) => {
  const category = getCategory(order);
  const labOrder = category === "lab" ? labOrders.get(order.id) : undefined;
  const queueOrder = queueOrders.get(order.id);
  const steps = category ? ORDER_DEF[category][1] : [];
  const isCancelled = order.order_status === ORDER_STATUS.CANCEL;
  const step = category
    ? getStep(category, order, parseStatus(queueOrder?.order_summary_detail_cache))
    : 0;
  const isLast = steps.length > 0 && step === steps.length - 1;
  const items = labOrder?.order_items?.length
    ? labOrder.order_items.map((item: any) => item.name as string)
    : [stripHtml(order.detail)];

  return {
    order,
    category,
    labOrder,
    queueOrder,
    isCancelled,
    isLocked: !isCancelled && isLast,
    canEdit:
      !isCancelled &&
      step === 0 &&
      !!order.can_edit &&
      (category === "lab" || EDITABLE_KINDS.includes(category as EditableKind)),
    canCancel: !isCancelled && !isLast && !!order.can_cancel,
    status: isCancelled ? "Cancelled" : (steps[step] ?? order.order_status_display),
    items,
  };
};

// ---------- ใบคำสั่ง ----------
/** แถวของใบคำสั่ง: Order ที่ยืนยันแล้ว + แลปร่าง + รายการร่าง + DF + เบ็ดเตล็ด แยกกลุ่มตามประเภท */
export const sheetGroupsOf = (params: SheetGroupsParams) => {
  const { pkgUse } = params;
  const lines = new Map<SheetCategory, SheetRow[]>();

  const push = (category: SheetCategory, line: SheetRow) => {
    lines.set(category, [...(lines.get(category) ?? []), line]);
  };

  for (const row of params.orderRows) {
    if (row.isCancelled || !row.category || params.editingOrderIds.has(row.order.id)) {
      continue;
    }

    const prices: any[] = row.order.price?.orders ?? [];
    const labItems: any[] = row.labOrder?.order_items ?? [];

    // ราคาต่อรายการของแลปเรียงตามลำดับ order_items
    if (labItems.length > 0 && labItems.length === prices.length) {
      for (const [index, item] of labItems.entries()) {
        push("lab", {
          key: `${row.order.id}_${item.id}`,
          name: item.name,
          outLab: item.lab_type_label === "Out lab",
          summary: URGENCY_LABELS[item.urgency] ?? item.urgency,
          amount: Number(prices[index]?.price_total || 0),
        });
      }
    } else {
      push(row.category, {
        key: `${row.order.id}`,
        name: stripHtml(row.order.detail),
        amount: Number(row.order.price?.price_total || 0),
      });
    }
  }

  for (const [index, item] of params.draftItems.entries()) {
    const key = `draft_${item.lab_code}`;
    const amount = Number(item.price_total || 0);

    push("lab", {
      key,
      name: item.name,
      outLab: item.lab_type_label === "Out lab",
      summary: formatLabSummary(item, params.labTestInfo[item.lab_code]),
      amount: pkgUse.has(key) ? 0 : amount,
      draftIndex: index,
      pkgText: formatPackage(pkgUse.get(key), amount),
    });
  }

  for (const line of params.sheetLines) {
    push(line.kind, {
      key: line.key,
      name: line.name,
      summary: sheetSummary(line, params.doctorOptions, params.masterOptions),
      amount: pkgUse.has(line.key) ? 0 : sheetAmount(line),
      pkgText: formatPackage(pkgUse.get(line.key), line.priceUnit),
      adr: line.raw.adr
        ? { adversary: line.raw.adr.adversary, reason: line.fields.adrNote ?? "" }
        : undefined,
      doseWarning: line.raw.doseWarning,
      draft: line,
      missing: sheetMissing(line),
    });
  }

  for (const item of params.doctorFeeList) {
    if (params.editingFeeIds.has(Number(item.id))) {
      continue;
    }

    push("df", {
      key: `df_${item.id}`,
      name: item.doctor_fee_rule_display ?? "",
      summary: item.received_employee_display
        ? `แพทย์ผู้รับ ${item.received_employee_display}`
        : "",
      amount: Number(item.price_total || 0),
    });
  }

  for (const item of params.miscList) {
    push("misc", {
      key: `misc_${item.id}`,
      name: item.name ?? "",
      summary: item.note ?? "",
      amount: Number(item.price_total || 0),
    });
  }

  return SHEET_CATEGORIES.filter((category) => lines.has(category)).map((category) =>
    sheetGroupOf(category, lines.get(category) ?? []),
  );
};

// ---------- คำสั่งที่ยืนยันแล้ว ----------
/** แถวของตารางคำสั่งที่ยืนยันแล้ว: Order + DF + เบ็ดเตล็ด */
export const doneRowsOf = (params: DoneRowsParams): DoneRow[] => [
  ...params.orderRows.flatMap((row) =>
    row.category
      ? [
          {
            ...row,
            key: `order_${row.order.id}`,
            category: row.category,
            isEditing: params.editingOrderIds.has(row.order.id),
          },
        ]
      : [],
  ),
  ...params.doctorFeeList.map((item) => ({
    key: `df_${item.id}`,
    category: "df" as const,
    status: FEE_STATUS,
    items: [item.doctor_fee_rule_display ?? ""],
    isCancelled: false,
    isEditing: params.editingFeeIds.has(Number(item.id)),
    isLocked: false,
    canEdit: !!item.id,
    canCancel: !!item.id,
    df: item,
  })),
  ...params.miscList.map((item) => ({
    key: `misc_${item.id}`,
    category: "misc" as const,
    status: FEE_STATUS,
    items: [[item.name, item.note].filter(Boolean).join(" · ")],
    isCancelled: false,
    isEditing: false,
    isLocked: false,
    canEdit: false,
    canCancel: !!item.id,
    misc: item,
  })),
];

export const doneGroupsOf = (rows: DoneRow[]): DoneGroup[] =>
  SHEET_CATEGORIES.map((category) => {
    const groupRows = rows.filter((row) => row.category === category);

    return {
      category,
      rows: groupRows,
      cancelled: groupRows.filter((row) => row.isCancelled).length,
      total: groupRows
        .filter((row) => !row.isCancelled)
        .reduce((sum, row) => sum + Number(priceOf(row) || 0), 0),
    };
  }).filter((group) => group.rows.length > 0);

/** แถบสรุป: คำสั่งที่ยืนยันแล้ว ไม่นับที่ยกเลิกและกำลังแก้ไข */
export const confirmedGroupsOf = (groups: DoneGroup[]) =>
  groups
    .map((group) => ({
      category: group.category,
      rows: group.rows.filter((row) => !row.isCancelled && !row.isEditing),
    }))
    .filter((group) => group.rows.length > 0);

export const summaryTextOf = (
  confirmedGroups: ReturnType<typeof confirmedGroupsOf>,
  pendingCount: number,
) => {
  const rows = confirmedGroups.flatMap((group) => group.rows);
  const total = rows.reduce((sum, row) => sum + Number(priceOf(row) || 0), 0);

  return [
    rows.length > 0 ? `ยืนยันแล้ว ${rows.length} รายการ` : "",
    rows.length > 0 ? `≈ ${formatComma(total, "en-US", false)} ฿` : "",
    pendingCount > 0 ? `รอยืนยัน ${pendingCount} รายการ` : "",
  ]
    .filter(Boolean)
    .join(" · ");
};
